import { DISCOVERY_EVENT_CATEGORIES, type DiscoverySyncTelemetry, parseDiscoveryInvalidation } from "./discovery";

export type DiscoverySyncState = "connected" | "reconnecting" | "degraded";

interface EventSourceLike {
	onopen: ((event: Event) => void) | null;
	onerror: ((event: Event) => void) | null;
	addEventListener(type: string, listener: (event: Event) => void): void;
	close(): void;
}

export interface DiscoveryLiveConnectionOptions {
	projectId: string;
	mapId: string;
	url: string;
	refresh: (minimumRevision?: number) => Promise<void>;
	onStateChange: (state: DiscoverySyncState) => void;
	latestAppliedRevision?: () => number | undefined;
	onTelemetry?: (telemetry: DiscoverySyncTelemetry) => void;
	createEventSource?: (url: string) => EventSourceLike;
	random?: () => number;
	setTimer?: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
	clearTimer?: (timer: ReturnType<typeof setTimeout>) => void;
	nowMs?: () => number;
}

const INITIAL_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

/** Owns one explicitly retried SSE connection; authoritative snapshots remain the only applied state. */
export class DiscoveryLiveConnection {
	private source: EventSourceLike | undefined;
	private retryTimer: ReturnType<typeof setTimeout> | undefined;
	private retryAttempt = 0;
	private convergenceRetryTimer: ReturnType<typeof setTimeout> | undefined;
	private convergenceRetryAttempt = 0;
	private minimumConvergenceRevision: number | undefined;
	private opened = false;
	private stopped = false;
	private degradedAt: number | undefined;
	private readonly createEventSource: (url: string) => EventSourceLike;
	private readonly random: () => number;
	private readonly setTimer: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
	private readonly clearTimer: (timer: ReturnType<typeof setTimeout>) => void;
	private readonly nowMs: () => number;

	constructor(private readonly options: DiscoveryLiveConnectionOptions) {
		this.createEventSource = options.createEventSource ?? ((url) => new EventSource(url, { withCredentials: true }));
		this.random = options.random ?? Math.random;
		this.setTimer = options.setTimer ?? ((callback, delay) => setTimeout(callback, delay));
		this.clearTimer = options.clearTimer ?? ((timer) => clearTimeout(timer));
		this.nowMs = options.nowMs ?? (() => performance.now());
	}

	start() {
		this.setState("reconnecting");
		this.connect();
	}

	stop() {
		this.finishDegradedPeriod();
		this.stopped = true;
		this.source?.close();
		this.source = undefined;
		if (this.retryTimer !== undefined) this.clearTimer(this.retryTimer);
		if (this.convergenceRetryTimer !== undefined) this.clearTimer(this.convergenceRetryTimer);
		this.retryTimer = undefined;
		this.convergenceRetryTimer = undefined;
	}

	/** Foreground and online recovery bypass a pending delay and always perform an authoritative read. */
	recover() {
		if (this.stopped) return Promise.resolve();
		if (this.convergenceRetryTimer !== undefined) this.clearTimer(this.convergenceRetryTimer);
		this.convergenceRetryTimer = undefined;
		if (!this.source) {
			if (this.retryTimer !== undefined) this.clearTimer(this.retryTimer);
			this.retryTimer = undefined;
			this.connect();
		}
		return this.refreshAndConverge();
	}

	/** Called after a successful local mutation's forced authoritative refresh. */
	noteAuthoritativeConvergence() {
		if (this.stopped) return;
		const appliedRevision = this.options.latestAppliedRevision?.();
		if (
			this.minimumConvergenceRevision !== undefined &&
			(appliedRevision === undefined || appliedRevision < this.minimumConvergenceRevision)
		) {
			this.setState("degraded");
			this.scheduleConvergenceRetry();
			return;
		}
		this.minimumConvergenceRevision = undefined;
		this.convergenceRetryAttempt = 0;
		if (this.convergenceRetryTimer !== undefined) this.clearTimer(this.convergenceRetryTimer);
		this.convergenceRetryTimer = undefined;
		this.setState(this.opened ? "connected" : "reconnecting");
	}

	private setState(state: DiscoverySyncState) {
		if (state === "degraded" && this.degradedAt === undefined) this.degradedAt = this.nowMs();
		if (state === "connected") this.finishDegradedPeriod();
		this.options.onStateChange(state);
	}

	private finishDegradedPeriod() {
		if (this.degradedAt === undefined) return;
		this.options.onTelemetry?.({
			signal: "degradedEnded",
			durationMs: Math.max(0, Math.round(this.nowMs() - this.degradedAt)),
		});
		this.degradedAt = undefined;
	}

	private connect() {
		if (this.stopped || this.source) return;
		this.opened = false;
		const source = this.createEventSource(this.options.url);
		this.source = source;
		source.onopen = () => {
			if (this.source !== source || this.stopped) return;
			this.opened = true;
			this.options.onTelemetry?.({ signal: "streamConnected", retryCount: this.retryAttempt });
			this.retryAttempt = 0;
			void this.refreshAndConverge();
		};
		source.onerror = () => {
			if (this.source !== source || this.stopped) return;
			source.close();
			this.source = undefined;
			this.opened = false;
			this.scheduleRetry();
		};
		for (const category of DISCOVERY_EVENT_CATEGORIES) {
			source.addEventListener(category, (rawEvent) => {
				if (this.source !== source || this.stopped) return;
				const event = parseDiscoveryInvalidation((rawEvent as MessageEvent<string>).data);
				if (!event || event.projectId !== this.options.projectId || event.mapId !== this.options.mapId) return;
				void this.refreshAndConverge(event.revision);
			});
		}
	}

	private async refreshAndConverge(minimumRevision?: number) {
		if (minimumRevision !== undefined)
			this.minimumConvergenceRevision = Math.max(this.minimumConvergenceRevision ?? -1, minimumRevision);
		try {
			await this.options.refresh(minimumRevision);
			const revision = this.options.latestAppliedRevision?.();
			const requiredRevision = this.minimumConvergenceRevision;
			if (requiredRevision !== undefined && (revision === undefined || revision < requiredRevision))
				throw new Error("The authoritative Discovery snapshot has not reached the emitted revision");
			if (requiredRevision !== undefined && revision !== undefined)
				this.options.onTelemetry?.({
					signal: "revisionApplied",
					emittedRevision: requiredRevision,
					revision,
				});
			this.noteAuthoritativeConvergence();
		} catch {
			this.setState("degraded");
			this.scheduleConvergenceRetry();
		}
	}

	private scheduleConvergenceRetry() {
		if (this.stopped || this.convergenceRetryTimer !== undefined) return;
		this.convergenceRetryAttempt += 1;
		const exponential = Math.min(MAX_RETRY_MS, INITIAL_RETRY_MS * 2 ** (this.convergenceRetryAttempt - 1));
		const jittered = Math.round(exponential * (0.75 + this.random() * 0.5));
		const delay = Math.min(MAX_RETRY_MS, jittered);
		this.convergenceRetryTimer = this.setTimer(() => {
			this.convergenceRetryTimer = undefined;
			void this.refreshAndConverge();
		}, delay);
	}

	private scheduleRetry() {
		this.retryAttempt += 1;
		this.options.onTelemetry?.({ signal: "streamRetry", retryCount: this.retryAttempt });
		this.setState(this.retryAttempt === 1 ? "reconnecting" : "degraded");
		const exponential = Math.min(MAX_RETRY_MS, INITIAL_RETRY_MS * 2 ** (this.retryAttempt - 1));
		const jittered = Math.round(exponential * (0.75 + this.random() * 0.5));
		const delay = Math.min(MAX_RETRY_MS, jittered);
		this.retryTimer = this.setTimer(() => {
			this.retryTimer = undefined;
			this.connect();
		}, delay);
	}
}
